/* eslint-disable no-console */
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { AdminRoute } from './components/AdminRoute';
import { HostRoute } from './components/HostRoute';
import { Layout } from './components/layout';
import { HostTourProvider } from './features/hostTour';
import { useAuth } from './hooks/useAuth';
import { useLanguagePreferenceSync } from './hooks/useLanguagePreferenceSync';
import ActivityDetail from './pages/ActivityDetail';
import AllActivities from './pages/AllActivities';
import ApplyHost from './pages/ApplyHost';
import BookingCheckout from './pages/BookingCheckout';
import BookingHostMessages from './pages/BookingHostMessages';
import BookingSuccess from './pages/BookingSuccess';
import ConfirmEmail from './pages/ConfirmEmail';
import ContactUs from './pages/ContactUs';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import GiftCardPurchase from './pages/GiftCardPurchase';
import GiftCardPurchaseSuccess from './pages/GiftCardPurchaseSuccess';
import GiftCardRedeem from './pages/GiftCardRedeem';
import GiftCardTerms from './pages/GiftCardTerms';
import Home from './pages/Home';
import Investors from './pages/Investors';
import Login from './pages/Login';
import PassCheckout from './pages/PassCheckout';
import PassDetail from './pages/PassDetail';
import PassPurchaseSuccess from './pages/PassPurchaseSuccess';
import PassRefundPolicy from './pages/PassRefundPolicy';
import PickDetail from './pages/PickDetail';
import PickSubmit from './pages/PickSubmit';
import PicksList from './pages/PicksList';
import RefundPolicy from './pages/RefundPolicy';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import TermsOfUse from './pages/TermsOfUse';
import TrainingProgram from './pages/TrainingProgram';
import UnfollowHost from './pages/UnfollowHost';
import UnsubscribeInvites from './pages/UnsubscribeInvites';
import WaitlistClaim from './pages/WaitlistClaim';
// Admin pages
import AdminActivities from './pages/admin/Activities';
import AdminActivityForm from './pages/admin/ActivityForm';
import AdminGiftCardPromotionForm from './pages/admin/AdminGiftCardPromotionForm';
import AdminGiftCards from './pages/admin/AdminGiftCards';
import AdminInvestorInquiries from './pages/admin/AdminInvestorInquiries';
import AdminMemberRewards from './pages/admin/AdminMemberRewards';
import AdminPickCandidates from './pages/admin/AdminPickCandidates';
import AdminPickForm from './pages/admin/AdminPickForm';
import AdminPickSubmissions from './pages/admin/AdminPickSubmissions';
import AdminPickTopics from './pages/admin/AdminPickTopics';
import AdminPicks from './pages/admin/AdminPicks';
import AdminAnalytics from './pages/admin/Analytics';
import AdminBookings from './pages/admin/Bookings';
import AdminCourseReview from './pages/admin/CourseReview';
import AdminDashboard from './pages/admin/Dashboard';
import AdminEmail from './pages/admin/Email';
import AdminHosts from './pages/admin/Hosts';
import AdminNewsletter from './pages/admin/Newsletter';
import AdminPassForm from './pages/admin/PassForm';
import AdminPasses from './pages/admin/Passes';
import AdminPromotionCodeForm from './pages/admin/PromotionCodeForm';
import AdminPromotionCodes from './pages/admin/PromotionCodes';
import ReviewModeration from './pages/admin/ReviewModeration';
import AdminSessionWaitlist from './pages/admin/SessionWaitlist';
import AdminSettlements from './pages/admin/Settlements';
import AdminUsers from './pages/admin/Users';
import CourseDetail from './pages/courses/CourseDetail';
import CoursePurchaseSuccess from './pages/courses/CoursePurchaseSuccess';
import CourseWatch from './pages/courses/CourseWatch';
import HostCourseEditor from './pages/host/HostCourseEditor';
import HostCourses from './pages/host/HostCourses';
// Host pages now reuse admin components with context detection
import { setAnalyticsEnabled, trackPageView } from './utils/analytics';
import { captureMemberCode } from './utils/memberCode';
import { consumeRedirect } from './utils/redirectAfterLogin';
import { captureUtmSource } from './utils/utmTracking';

function App() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  // The language switcher follows the account across devices (PRD §5.6).
  useLanguagePreferenceSync(!authLoading && user ? user.userId : null);

  // Enable GA4 tracking only after auth resolves and confirms user is not admin/host.
  // Defaults to disabled to prevent events firing before auth check completes.
  useEffect(() => {
    if (authLoading) return; // Wait for auth to resolve
    setAnalyticsEnabled(!user?.isAdmin && !user?.isHost);
  }, [user, authLoading]);

  // Capture UTM source from URL for attribution tracking
  useEffect(() => {
    captureUtmSource();
  }, []);

  // Remember a member's promo code from a shared link (member rewards PRD §5.2)
  useEffect(() => {
    captureMemberCode(location.search);
  }, [location.search]);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Handle OAuth callback
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          getCurrentUser()
            .then(() => {
              // If a redirect was stashed before the OAuth round-trip, honor it.
              const target = consumeRedirect() ?? '/';
              window.location.href = target;
            })
            .catch((err) => {
              console.error('OAuth callback error:', err);
            });
          break;
        case 'signInWithRedirect_failure':
          console.error('OAuth sign in failed:', payload.data);
          break;
      }
    });

    return unsubscribe;
  }, []);
  return (
    <HostTourProvider>
      <Routes>
        {/* Auth and checkout-result pages share the public header and footer
            (docs/prd-homepage-redesign.md AC20); the back office keeps AdminLayout. */}
        {(
          [
            ['/login', <Login key="login" />],
            ['/register', <Register key="register" />],
            ['/confirm-email', <ConfirmEmail key="confirm" />],
            ['/forgot-password', <ForgotPassword key="forgot" />],
            ['/reset-password', <ResetPassword key="reset" />],
            ['/booking-success', <BookingSuccess key="booking-success" />],
            ['/pass-purchase-success', <PassPurchaseSuccess key="pass-success" />],
          ] as const
        ).map(([path, page]) => (
          <Route key={path} path={path} element={<Layout>{page}</Layout>} />
        ))}

        {/* Admin routes - protected by AdminRoute */}
        <Route path="/admin" element={<Navigate to="/admin/activities" replace />} />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/activities"
          element={
            <AdminRoute>
              <AdminActivities />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/activities/new"
          element={
            <AdminRoute>
              <AdminActivityForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/activities/:id/edit"
          element={
            <AdminRoute>
              <AdminActivityForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/course-review"
          element={
            <AdminRoute>
              <AdminCourseReview />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <AdminRoute>
              <AdminBookings />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/settlements"
          element={
            <AdminRoute>
              <AdminSettlements />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/investor-inquiries"
          element={
            <AdminRoute>
              <AdminInvestorInquiries />
            </AdminRoute>
          }
        />
        {/* YOYO Picks (PRD §5.8) */}
        <Route
          path="/admin/picks"
          element={
            <AdminRoute>
              <AdminPicks />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/picks/new"
          element={
            <AdminRoute>
              <AdminPickForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/picks/submissions"
          element={
            <AdminRoute>
              <AdminPickSubmissions />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/picks/candidates"
          element={
            <AdminRoute>
              <AdminPickCandidates />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/picks/topics"
          element={
            <AdminRoute>
              <AdminPickTopics />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/picks/:id"
          element={
            <AdminRoute>
              <AdminPickForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reviews"
          element={
            <AdminRoute>
              <ReviewModeration />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/passes"
          element={
            <AdminRoute>
              <AdminPasses />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/passes/new"
          element={
            <AdminRoute>
              <AdminPassForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/passes/:id/edit"
          element={
            <AdminRoute>
              <AdminPassForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/member-rewards"
          element={
            <AdminRoute>
              <AdminMemberRewards />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/promotion-codes"
          element={
            <AdminRoute>
              <AdminPromotionCodes />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/promotion-codes/new"
          element={
            <AdminRoute>
              <AdminPromotionCodeForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/promotion-codes/:id/edit"
          element={
            <AdminRoute>
              <AdminPromotionCodeForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/gift-cards"
          element={
            <AdminRoute>
              <AdminGiftCards />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/gift-cards/promotions/new"
          element={
            <AdminRoute>
              <AdminGiftCardPromotionForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/gift-cards/promotions/:id/edit"
          element={
            <AdminRoute>
              <AdminGiftCardPromotionForm />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/email"
          element={
            <AdminRoute>
              <AdminEmail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/newsletter"
          element={
            <AdminRoute>
              <AdminNewsletter />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <AdminRoute>
              <AdminAnalytics />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/hosts"
          element={
            <AdminRoute>
              <AdminHosts />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/sessions/:sessionId/waitlist"
          element={
            <AdminRoute>
              <AdminSessionWaitlist />
            </AdminRoute>
          }
        />

        {/* Host routes - protected by HostRoute */}
        <Route
          path="/host/activities"
          element={
            <HostRoute>
              <AdminActivities />
            </HostRoute>
          }
        />
        <Route
          path="/host/activities/new"
          element={
            <HostRoute>
              <AdminActivityForm />
            </HostRoute>
          }
        />
        <Route
          path="/host/courses"
          element={
            <HostRoute>
              <HostCourses />
            </HostRoute>
          }
        />
        <Route
          path="/host/courses/new"
          element={
            <HostRoute>
              <HostCourseEditor />
            </HostRoute>
          }
        />
        <Route
          path="/host/courses/:id/edit"
          element={
            <HostRoute>
              <HostCourseEditor />
            </HostRoute>
          }
        />
        <Route
          path="/host/activities/:id/edit"
          element={
            <HostRoute>
              <AdminActivityForm />
            </HostRoute>
          }
        />
        <Route
          path="/host/passes"
          element={
            <HostRoute>
              <AdminPasses />
            </HostRoute>
          }
        />
        <Route
          path="/host/passes/new"
          element={
            <HostRoute>
              <AdminPassForm />
            </HostRoute>
          }
        />
        <Route
          path="/host/passes/:id/edit"
          element={
            <HostRoute>
              <AdminPassForm />
            </HostRoute>
          }
        />
        <Route
          path="/host/bookings"
          element={
            <HostRoute>
              <AdminBookings />
            </HostRoute>
          }
        />
        <Route
          path="/host/email"
          element={
            <HostRoute>
              <AdminEmail />
            </HostRoute>
          }
        />

        {/* App routes - with layout */}
        <Route
          path="/*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/my-bookings"
                  element={<Navigate to="/dashboard?tab=bookings" replace />}
                />
                {/* Host-published event detail + reminder content for one booking */}
                <Route path="/bookings/:bookingId/details" element={<BookingHostMessages />} />
                <Route path="/activities" element={<AllActivities />} />
                <Route path="/activity/:slug" element={<ActivityDetail />} />
                {/* YOYO Picks: third-party programs and events (PRD §5) */}
                <Route path="/picks" element={<PicksList />} />
                <Route path="/picks/submit" element={<PickSubmit />} />
                <Route path="/investors" element={<Investors />} />
                <Route path="/picks/:slug" element={<PickDetail />} />
                {/* Recorded courses: detail, watch, purchase success. There is no
                  standalone catalog for now — courses live in /activities; keep the
                  /courses redirect so old links don't 404. More specific paths
                  first so /:slug does not swallow them. */}
                <Route path="/courses" element={<Navigate to="/activities" replace />} />
                <Route path="/courses/:slug/watch" element={<CourseWatch />} />
                <Route path="/courses/:slug/purchase-success" element={<CoursePurchaseSuccess />} />
                <Route path="/courses/:slug" element={<CourseDetail />} />
                <Route path="/waitlist/claim/:waitlistId" element={<WaitlistClaim />} />
                {/* Reached from a host update email; the signed token in the query
                  is the authorization, so no login guard here. */}
                <Route path="/unfollow/host" element={<UnfollowHost />} />
                <Route path="/unsubscribe/invites" element={<UnsubscribeInvites />} />
                <Route path="/booking/:slug" element={<BookingCheckout />} />
                <Route path="/passes/:slug" element={<PassDetail />} />
                <Route path="/passes/:id/checkout" element={<PassCheckout />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/pass-refund-policy" element={<PassRefundPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/apply-host" element={<ApplyHost />} />
                <Route path="/training-program" element={<TrainingProgram />} />
                {/* Gift card public pages: purchase is the default landing; redeem
                  is reached via a button in the purchase page's top-right. */}
                <Route path="/gift-cards" element={<GiftCardPurchase />} />
                <Route path="/gift-cards/redeem" element={<GiftCardRedeem />} />
                <Route path="/gift-cards/success" element={<GiftCardPurchaseSuccess />} />
                <Route path="/gift-cards/terms" element={<GiftCardTerms />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>
    </HostTourProvider>
  );
}

export default App;
